import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Center, Flex, Text, TabPanel, Tabs } from "@chakra-ui/react";
import { ActionBar, Layout, PageMotion, PoweredBy, TabPanels, CicoTabAddress } from "components";
import configs from "config";
import { useLocation, useNavigate } from "react-router";
import { CicoFiat, CicoKYC, CicoQuote, CicoTransfer } from "ui";
import ls from "utils/secureStorage";
import { selectForm, setCicoAuthCheck } from "store/slices";
import { useWallet } from "hooks";

export const plusSomeSeconds = (date: Date, seconds: number) => {
  const givenDate = date ? new Date(date) : new Date();
  const workingDate = new Date(givenDate);
  workingDate.setSeconds(workingDate.getSeconds() + seconds);

  return workingDate;
};

declare global {
  interface Window {
    ethereum: any;
  }
}

const CICO = () => {
  const navigate = useNavigate();

  const { isConnected, setIsConnected, haveMetamask, handleConnectWallet } = useWallet();

  const cicoToken = useMemo(() => ls.get(configs.CICO_TOKEN_KEY), []);

  const { cicoAuthCheck, sidebarClick } = useSelector(selectForm);

  const dispatch = useDispatch();

  useEffect(() => {
    if (cicoToken) {
      dispatch(setCicoAuthCheck(true));
    }
  }, [cicoToken, dispatch]);

  const location = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(location?.search as string), [location?.search]);

  const tabMap = {
    quote: 0,
    transfers: 1,
    fiat: 2,
    kyc: 3,
  };

  const [tabIndex, setTabIndex] = useState((tabMap as any)[searchParams.get("tab") as any] ?? 0);
  const handleTabChange = (index: number) => {
    setTabIndex(index);
    searchParams.set("tab", Object.keys(tabMap)[index]);
    navigate(`?${searchParams.toString()}`);
  };

  const tabData = useMemo(() => {
    return [
      {
        label: "Quotes",
        value: "quotes",
        index: 0,
      },
      {
        label: "Transfers",
        value: "transfers",
        index: 1,
      },
      {
        label: "Fiat",
        value: "fiat",
        index: 2,
      },
      {
        label: "KYC",
        value: "kyc",
        index: 3,
      },
    ];
  }, []);

  const handleRadioTab = (tab: string) => {
    const index = tabData.findIndex(({ value }) => value === tab);

    handleTabChange(index);
  };

  const defaultChecked = useMemo(() => {
    return tabData.filter(({ index }) => tabIndex === index)[0].value;
  }, [tabData, tabIndex]);

  useEffect(() => {
    sidebarClick && handleRadioTab(sidebarClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarClick]);

  const handleDisconnect = () => {
    dispatch(setCicoAuthCheck(false));
    ls.remove("cico_address");
    ls.remove("cico_balance");
    ls.remove("wallet_balance");
    ls.remove(configs.CICO_TOKEN_KEY);
    ls.remove(configs.CICO_QUOTE_ID);
    ls.remove(configs.CICO_TYPE);

    setIsConnected(false);

    window.location.replace("/cico");
  };

  return (
    <PageMotion px={{ base: "8px", "1sm": "4px" }}>
      <Layout _main={{ maxW: { sm: "480px", "3sm": "820px" }, margin: "auto", minH: "85vh", padding: "0" }}>
        <Box m="0 auto" pos="relative">
          <ActionBar onBackClick={() => navigate(-1)} title="CICO" />
        </Box>

        <Box mt="24px" border="1px solid #F2F4F3" borderRadius="8px" pl={{ base: "0", "3sm": "16px" }} minH="80vh">
          {!haveMetamask && (
            <Text textAlign="center" fontWeight="500" mt="82px">
              Please, Install Metamask
            </Text>
          )}

          {!isConnected && haveMetamask && (
            <Center height="50vh">
              <Button onClick={handleConnectWallet}>Connect Wallet</Button>
            </Center>
          )}

          <Flex gridGap={{ base: "0", "3sm": "18px" }}>
            <CicoTabAddress
              cicoAuthCheck={cicoAuthCheck}
              defaultChecked={defaultChecked}
              handleDisconnect={handleDisconnect}
              handleRadioTab={handleRadioTab}
              haveMetamask={haveMetamask}
              isConnected={isConnected}
              tabData={tabData}
            />

            <Box width="100%">
              {isConnected && (
                <Tabs isLazy variant="ghost" index={tabIndex} onChange={handleTabChange}>
                  <TabPanels index={tabIndex} padding={{ base: "38px 8px 16px" }}>
                    <TabPanel padding="0 16px">
                      <CicoQuote handleTabChange={handleTabChange} />
                    </TabPanel>

                    <TabPanel padding="0 16px">
                      <CicoTransfer tabChange={handleTabChange} />
                    </TabPanel>

                    <TabPanel padding="0 16px">
                      <CicoFiat />
                    </TabPanel>

                    <TabPanel padding="0 16px">
                      <CicoKYC />
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              )}
            </Box>
          </Flex>
        </Box>
      </Layout>

      <PoweredBy />
    </PageMotion>
  );
};

export default CICO;
